import { Vector3, type Object3D } from 'three';
import type { WorkKind } from '../audio/cues';
import { BALANCE } from '../sim/balance';
import { ITEMS, item, toolFor } from '../sim/items';
import { canPickUp } from '../sim/inventory';
import type { ItemId, LockKind, PlayerState, Vec3 } from '../sim/types';
import { LOCK_LABEL } from '../world/fixtures';
import type { BuiltMall } from '../world/plan';
import type { Round } from '../sim/round';
import type { MallPhysics } from './physics';

/**
 * INTERACTION.
 *
 * One target at a time, chosen by where you are looking, and one verb: hold the
 * key. Everything in the mall — a shutter, a vitrine, a shelf, the pneumatic
 * chute, the loading dock, a bag of loot on the floor — is the same gesture with
 * a different duration, and the duration is the design. A crowbar on a shutter is
 * three seconds; a drill on a safe is twenty-two, and you are standing still and
 * making noise for all of them.
 *
 * TARGETING IS NOT A RAYCAST. It is a proximity-and-angle score with a line of
 * sight check. A pure crosshair raycast is precise and horrible to use: the
 * player has to aim at a shelf edge rather than at a shelf, and every fixture in
 * the mall is made of thin boxes with gaps between them. Scoring by "closest
 * thing near the middle of the screen that I can actually see" is what every
 * shipped first-person game does, and it is invisible when it works.
 *
 * The line of sight check is the part that must not be skipped. Without it you
 * can loot a vitrine through the shutter you have not cut yet, which quietly
 * deletes the entire lock system.
 */

export type TargetKind = 'container' | 'barrier' | 'dropoff' | 'loose';

export interface Target {
  kind: TargetKind;
  id: string;
  /** What the fixture is called, in the HUD's voice. */
  title: string;
  /** The line under it. Contents, payout rate, what it needs. */
  detail: string;
  /** Where to aim the highlight and the line of sight ray. */
  at: Vector3;
  node: Object3D | null;
  /** Seconds of holding required. Zero means it fires on tap. */
  duration: number;
  /** Null if the action is available. Otherwise why it is not. */
  blocked: string | null;
  /** Key hint, e.g. "HOLD E". */
  verb: string;
  /** Noise radius the action will make on completion. Shown as a warning. */
  noise: number;
  /**
   * What holding the key sounds like.
   *
   * Carried on the target rather than inferred by the audio layer, because this
   * is the only place that already knows a shutter is being cut and a floor safe
   * is being drilled. It maps one-to-one onto the lock kinds by design: the
   * loudness of a breach is a balance number, and the sound of it should never
   * be able to disagree with the number.
   */
  work: WorkKind;
}

export interface LooseLoot {
  id: string;
  item: ItemId;
  pos: Vec3;
  store: string | null;
  node: Object3D;
}

/** One player's hold on one fixture. */
interface Hold {
  progress: number;
  /** `kind:id` of the fixture being held, or null. */
  id: string | null;
  duration: number;
  /** The target a still-held key has already finished. See `update`. */
  settled: string | null;
}

interface Candidate {
  kind: TargetKind;
  id: string;
  at: Vector3;
  reach: number;
  node: Object3D | null;
}

/** Extra metres of reach beyond the fixture's own, to forgive standing back. */
const REACH_SLACK = 0.7;
/** How far off the crosshair a target may be. cos(50°). */
const MIN_FACING = 0.64;
/** Seconds per item pulled off a shelf. Looting a full shelf is not instant. */
const LOOT_SEC = 0.55;

export class Interaction {
  /** Loot lying on the floor, with the mesh that represents it. */
  readonly loose: LooseLoot[] = [];

  private readonly candidates: Candidate[] = [];
  private readonly lookAt = new Vector3();
  private readonly toTarget = new Vector3();

  /**
   * One hold per player, not one hold.
   *
   * This used to be three fields on the class, which was correct for exactly as
   * long as there was one pair of hands in the building. Under host authority the
   * host advances the hold for everybody — four thieves can be cutting four
   * shutters at once, and the progress ring on each of their screens is their own
   * — so the state is keyed by player id and the class itself is stateless
   * between them.
   */
  private readonly holds = new Map<string, Hold>();

  private hold(id: string): Hold {
    let h = this.holds.get(id);
    if (!h) {
      h = { progress: 0, id: null, duration: 0, settled: null };
      this.holds.set(id, h);
    }
    return h;
  }

  /** 0..1 while a hold is in progress. The HUD draws the ring for one player. */
  progressFor(playerId: string): number {
    return this.holds.get(playerId)?.progress ?? 0;
  }

  /** Everything this player was in the middle of, forgotten. */
  forget(playerId: string): void {
    this.holds.delete(playerId);
  }

  constructor(
    private readonly mall: BuiltMall,
    private readonly physics: MallPhysics,
    private readonly round: Round,
  ) {
    for (const c of mall.containers) {
      this.candidates.push({
        kind: 'container',
        id: c.id,
        at: new Vector3(c.pos.x, c.pos.y + c.aimY, c.pos.z),
        reach: c.reach,
        node: c.node,
      });
    }
    for (const b of mall.barriers) {
      this.candidates.push({
        kind: 'barrier',
        id: b.id,
        at: new Vector3(b.pos.x, b.pos.y, b.pos.z),
        reach: 2.0,
        node: b.node,
      });
    }
    for (const d of mall.dropoffs) {
      this.candidates.push({
        kind: 'dropoff',
        id: d.id,
        at: new Vector3(d.pos.x, d.pos.y + 1.1, d.pos.z),
        reach: Math.max(1.8, d.radius),
        node: null,
      });
    }
  }

  addLoose(l: LooseLoot): void {
    this.loose.push(l);
    this.candidates.push({
      kind: 'loose',
      id: l.id,
      at: new Vector3(l.pos.x, l.pos.y + 0.25, l.pos.z),
      // Tight on purpose. A fixture is a metre wide and you should be able to
      // use it from a pace back; a dropped ring is two centimetres and you
      // should have to stand on it. The loose reach is also what decides which
      // of five tools laid out on one bench the crosshair means.
      reach: 1.1,
      node: l.node,
    });
  }

  /**
   * Somebody else picked that up.
   *
   * The client is told what happened rather than working it out, so it needs the
   * same removal the host performs inside `commit` — otherwise the mesh stays on
   * the floor and the prompt keeps offering a ring that is already in a bag.
   */
  takeLooseRemotely(id: string): void {
    this.removeLoose(id)?.node.removeFromParent();
  }

  private removeLoose(id: string): LooseLoot | null {
    const i = this.loose.findIndex((l) => l.id === id);
    if (i < 0) return null;
    const [l] = this.loose.splice(i, 1);
    const c = this.candidates.findIndex((x) => x.kind === 'loose' && x.id === id);
    if (c >= 0) this.candidates.splice(c, 1);
    return l;
  }

  /**
   * The thing the player is about to act on, or null.
   *
   * Scored rather than raycast; see the header. The score rewards being close and
   * being centred, and the line of sight test is last because it is the only part
   * that costs anything.
   */
  find(eye: Vector3, forward: Vector3, p: PlayerState): Target | null {
    let best: Candidate | null = null;
    let bestScore = -Infinity;

    for (const c of this.candidates) {
      this.toTarget.copy(c.at).sub(eye);
      const dist = this.toTarget.length();
      const limit = c.reach + REACH_SLACK;
      if (dist > limit || dist < 1e-4) continue;
      this.toTarget.divideScalar(dist);
      const facing = this.toTarget.dot(forward);
      if (facing < MIN_FACING) continue;
      // Centredness dominates; proximity breaks ties. Two shelves side by side
      // should switch on where you look, not on a centimetre of standing.
      const score = facing * 2.2 - dist / limit;
      if (score <= bestScore) continue;
      if (!this.canSee(eye, c.at, dist)) continue;
      best = c;
      bestScore = score;
    }
    if (!best) return null;
    return this.describe(best, p);
  }

  private canSee(eye: Vector3, at: Vector3, dist: number): boolean {
    this.lookAt.copy(at).sub(eye).divideScalar(dist);
    const toi = this.physics.ray(eye, this.lookAt, dist - 0.25);
    return toi === null;
  }

  private describe(c: Candidate, p: PlayerState): Target {
    switch (c.kind) {
      case 'container':
        return this.describeContainer(c, p);
      case 'barrier':
        return this.describeBarrier(c, p);
      case 'dropoff':
        return this.describeDropoff(c, p);
      default:
        return this.describeLoose(c, p);
    }
  }

  private describeContainer(c: Candidate, p: PlayerState): Target {
    const state = this.round.containers.get(c.id)!;
    const store = this.mall.containers.find((x) => x.id === c.id)!;
    const title = FIXTURE_NAME[state.lock] ?? 'Container';
    if (!state.open) return lockTarget(c, title, state.lock, p);

    if (state.contents.length === 0) {
      return {
        kind: 'container',
        id: c.id,
        title,
        detail: 'EMPTY',
        at: c.at,
        node: c.node,
        duration: 0,
        blocked: 'empty',
        verb: '',
        noise: 0,
        work: 'none',
      };
    }
    const next = bestTakeable(state.contents, p);
    const worth = state.contents.reduce((s, i) => s + ITEMS[i].value, 0);
    if (!next) {
      const why = canPickUp(p, sortByValue(state.contents)[0]).reason;
      return {
        kind: 'container',
        id: c.id,
        title,
        detail: `${state.contents.length} items · $${worth.toLocaleString()}`,
        at: c.at,
        node: c.node,
        duration: 0,
        blocked: why === 'too-heavy' ? 'TOO HEAVY' : 'HANDS FULL',
        verb: '',
        noise: 0,
        work: 'none',
      };
    }
    const def = ITEMS[next];
    void store;
    return {
      kind: 'container',
      id: c.id,
      title,
      detail: `${def.name} · $${def.value.toLocaleString()} · ${def.weight}kg  (${state.contents.length} left)`,
      at: c.at,
      node: c.node,
      duration: LOOT_SEC,
      blocked: null,
      verb: 'HOLD E — TAKE',
      noise: 0,
      work: 'take',
    };
  }

  private describeBarrier(c: Candidate, p: PlayerState): Target {
    const b = this.mall.barriers.find((x) => x.id === c.id)!;
    if (!b.node.visible) {
      return blank(c, b.label.split(' — ')[0], 'OPEN');
    }
    return lockTarget(c, b.label.split(' — ')[0], b.lock, p, 'SHUTTER');
  }

  private describeDropoff(c: Candidate, p: PlayerState): Target {
    const d = this.mall.dropoffs.find((x) => x.id === c.id)!;
    const loot = p.carried.filter((s) => ITEMS[s.item]?.kind === 'loot');
    const value = loot.reduce((s, x) => s + ITEMS[x.item].value, 0);

    if (d.kind === 'extraction') {
      const take = Math.round(value * BALANCE.dropoff.extraction.payoutRate);
      return {
        kind: 'dropoff',
        id: c.id,
        title: 'EXIT',
        detail: value
          ? `Leave now. Banks $${take.toLocaleString()} of $${value.toLocaleString()} — and ends your round.`
          : 'Leave now. Ends your round.',
        at: c.at,
        node: null,
        duration: 2.0,
        blocked: null,
        verb: 'HOLD E — LEAVE',
        noise: 0,
        work: 'leave',
      };
    }

    if (loot.length === 0) return blank(c, DROP_NAME[d.kind], 'NOTHING TO DEPOSIT');

    if (d.kind === 'solo') {
      const cfg = BALANCE.dropoff.solo;
      return {
        kind: 'dropoff',
        id: c.id,
        title: DROP_NAME.solo,
        detail: `${loot.length} items · pays ${Math.round(cfg.payoutRate * 100)}% = $${Math.round(
          value * cfg.payoutRate,
        ).toLocaleString()}`,
        at: c.at,
        node: null,
        duration: loot.length * cfg.secPerItem,
        blocked: null,
        verb: 'HOLD E — POST',
        noise: cfg.noise,
        work: 'deposit',
      };
    }

    const cfg = BALANCE.dropoff.dock;
    let helpers = 0;
    for (const other of this.round.players.values()) {
      if (other.id === p.id || other.team !== 'thief' || other.status !== 'active') continue;
      if (distance(other.pos, d.pos) <= cfg.assistRadius) helpers++;
    }
    const rate = cfg.payoutRate * (1 + cfg.teamBonus);
    const short = helpers + 1 < cfg.minThieves;
    return {
      kind: 'dropoff',
      id: c.id,
      title: DROP_NAME.dock,
      detail: short
        ? `Needs ${cfg.minThieves} thieves on the door. ${helpers + 1} here.`
        : `${loot.length} items · pays ${Math.round(rate * 100)}% = $${Math.round(value * rate).toLocaleString()}`,
      at: c.at,
      node: null,
      duration: loot.length * cfg.secPerItem,
      blocked: short ? 'ONE MORE THIEF' : null,
      verb: 'HOLD E — LOAD',
      noise: cfg.noise,
      work: 'deposit',
    };
  }

  private describeLoose(c: Candidate, p: PlayerState): Target {
    const l = this.loose.find((x) => x.id === c.id)!;
    const def = ITEMS[l.item];
    const check = canPickUp(p, l.item);
    return {
      kind: 'loose',
      id: c.id,
      title: def.name,
      detail: def.kind === 'loot' ? `$${def.value.toLocaleString()} · ${def.weight}kg` : (def.blurb ?? def.name),
      at: c.at,
      node: c.node,
      duration: 0.25,
      blocked: check.ok ? null : check.reason === 'too-heavy' ? 'TOO HEAVY' : 'HANDS FULL',
      verb: 'HOLD E — TAKE',
      noise: 0,
      work: 'take',
    };
  }

  /**
   * Advances a hold. Returns a result the session turns into HUD toasts and
   * world changes. Called every frame with whether the key is down.
   */
  update(dt: number, target: Target | null, held: boolean, p: PlayerState): InteractionResult | null {
    const h = this.hold(p.id);
    if (!target || target.blocked || !held) {
      // Progress decays rather than snapping to zero, so a momentary loss of
      // target while a grinder is running does not throw away eight seconds.
      h.progress = Math.max(0, h.progress - dt * 1.6);
      if (h.progress === 0) h.id = null;
      if (!held) h.settled = null;
      return null;
    }
    const key = `${target.kind}:${target.id}`;
    // Keeping the key down repeats on the SAME fixture — that is how you empty a
    // shelf, and it is right. It must not roll onto the NEXT fixture, because
    // the tools are laid out 50cm apart on one bench and a single one-second
    // press would otherwise sweep up the whole table and pick your loadout for
    // you. Crossing to a different target costs a release.
    if (h.settled && h.settled !== key) return null;
    if (h.id !== key) {
      h.id = key;
      h.progress = 0;
      h.duration = target.duration;
    }
    if (h.duration <= 0) {
      h.progress = 0;
      h.id = null;
      h.settled = key;
      return this.commit(target, p);
    }
    h.progress += dt / h.duration;
    if (h.progress < 1) return null;
    h.progress = 0;
    h.id = null;
    h.settled = key;
    return this.commit(target, p);
  }

  private commit(target: Target, p: PlayerState): InteractionResult | null {
    switch (target.kind) {
      case 'container': {
        const state = this.round.containers.get(target.id)!;
        if (!state.open) {
          const tool = toolFor(state.lock);
          const r = this.round.breach(p.id, target.id, tool?.id ?? null);
          if (!r.ok) return { kind: 'refused', message: r.reason.toUpperCase() };
          return { kind: 'breached', id: target.id, lock: state.lock, noise: target.noise };
        }
        const next = bestTakeable(state.contents, p);
        if (!next) return null;
        const r = this.round.takeFrom(p.id, target.id, next);
        if (!r.ok) return { kind: 'refused', message: r.reason.toUpperCase() };
        return { kind: 'looted', item: next, id: target.id };
      }
      case 'barrier': {
        const b = this.mall.barriers.find((x) => x.id === target.id)!;
        const tool = toolFor(b.lock);
        if (b.lock !== 'none' && (!tool || !p.tools.includes(tool.id))) {
          return { kind: 'refused', message: 'NO TOOL' };
        }
        b.node.visible = false;
        this.physics.openBarrier(target.id);
        return { kind: 'breached', id: target.id, lock: b.lock, noise: target.noise };
      }
      case 'dropoff': {
        const d = this.mall.dropoffs.find((x) => x.id === target.id)!;
        const r = d.kind === 'extraction' ? this.round.extract(p.id, d.id) : this.round.deposit(p.id, d.id);
        if (!r.ok) return { kind: 'refused', message: r.reason.toUpperCase() };
        return {
          kind: 'deposited',
          amount: r.amount,
          items: r.items,
          extraction: d.kind === 'extraction',
        };
      }
      default: {
        const r = this.round.takeLoose(p.id, target.id);
        if (!r.ok) return { kind: 'refused', message: r.reason.toUpperCase() };
        const l = this.removeLoose(target.id);
        if (l) l.node.removeFromParent();
        return { kind: 'looted', item: l!.item, id: target.id };
      }
    }
  }
}

export type InteractionResult =
  | { kind: 'refused'; message: string }
  | { kind: 'breached'; id: string; lock: LockKind; noise: number }
  | { kind: 'looted'; item: ItemId; id: string }
  | { kind: 'deposited'; amount: number; items: number; extraction: boolean };

const FIXTURE_NAME: Partial<Record<LockKind, string>> = {
  none: 'Shelving',
  force: 'Stock Cabinet',
  pick: 'Controlled Cabinet',
  card: 'Staff Cabinet',
  cut: 'Vitrine',
  safe: 'Floor Safe',
};

const DROP_NAME: Record<string, string> = {
  solo: 'PNEUMATIC CHUTE',
  dock: 'LOADING DOCK',
  extraction: 'EXIT',
};

function blank(c: Candidate, title: string, detail: string): Target {
  return {
    kind: c.kind,
    id: c.id,
    title,
    detail,
    at: c.at,
    node: c.node,
    duration: 0,
    blocked: detail,
    verb: '',
    noise: 0,
    work: 'none',
  };
}

/**
 * The shared shape of "this is shut and here is what would open it". Used by
 * both containers and shutters so a locked vitrine and a locked shopfront read
 * identically — the player learns one rule.
 */
function lockTarget(c: Candidate, title: string, lock: LockKind, p: PlayerState, suffix = ''): Target {
  const tool = toolFor(lock);
  if (!tool) return blank(c, title, 'OPEN');
  const have = p.tools.includes(tool.id);
  const label = suffix ? `${title} — ${suffix}` : title;
  return {
    kind: c.kind,
    id: c.id,
    title: label,
    detail: have
      ? `${LOCK_LABEL[lock]} · ${tool.name} · ${tool.breachSec}s · noise ${tool.noise}m`
      : `${LOCK_LABEL[lock]} · needs ${tool.name}`,
    at: c.at,
    node: c.node,
    duration: tool.breachSec ?? 3,
    blocked: have ? null : `NEEDS ${tool.name.toUpperCase()}`,
    verb: 'HOLD E — BREACH',
    noise: tool.noise ?? 0,
    // The lock kinds and the work sounds share a vocabulary on purpose; see the
    // note on `Target.work`.
    work: lock as WorkKind,
  };
}

/** The most valuable thing in there that the player can actually lift. */
function bestTakeable(contents: ItemId[], p: PlayerState): ItemId | null {
  for (const id of sortByValue(contents)) {
    if (canPickUp(p, id).ok) return id;
  }
  return null;
}

function sortByValue(contents: ItemId[]): ItemId[] {
  return [...contents].sort((a, b) => {
    const av = item(a);
    const bv = item(b);
    // Tools and consumables sort above cheap loot: a crowbar on a skate shop
    // shelf is worth far more to the round than six decks, and sorting purely by
    // dollar value would bury it.
    const rank = (d: typeof av) => (d.kind === 'tool' ? 1e7 : d.kind === 'consumable' ? 1e6 : d.value);
    return rank(bv) - rank(av);
  });
}

function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}
